import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, euro, dataIt } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EntityFormDialog } from "@/components/EntityFormDialog";
import { CLIENTE_FIELDS } from "@/pages/Clienti";
import { biciFields } from "@/pages/Bici";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { PrivacyCard } from "@/components/PrivacyCard";
import { ArchivioDocumenti } from "@/components/ArchivioDocumenti";

export default function ClienteDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [bici, setBici] = useState([]);
  const [schede, setSchede] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [biciOpen, setBiciOpen] = useState(false);
  const [negozio, setNegozio] = useState("");
  useEffect(() => { api.get("/impostazioni").then((r) => setNegozio(r.data.nome_negozio || "")); }, []);

  const load = useCallback(async () => {
    const [a, b, s] = await Promise.all([api.get(`/clienti/${id}`), api.get("/bici"), api.get("/schede", { params: { cliente_id: id } })]);
    setC(a.data);
    setBici(b.data.filter((x) => x.cliente_id === id));
    setSchede(s.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;

  return (
    <div>
      <Link to="/clienti" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="cliente-back-link"><ArrowLeft className="h-4 w-4" /> Clienti</Link>
      <PageHeader overline="Scheda cliente" title={`${c.nome} ${c.cognome}`}>
        <WhatsAppButton telefono={c.telefono} testId="cliente-whatsapp-button" />
        <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="cliente-edit-button"><Pencil className="mr-1 h-4 w-4" /> Modifica</Button>
        <Button onClick={() => navigate(`/schede/nuova?cliente=${id}`)} className="bg-orange-600 hover:bg-orange-700" data-testid="cliente-new-scheda-button"><Plus className="mr-1 h-4 w-4" /> Nuova scheda</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-6">
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-5 text-sm shadow-sm">
          <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {c.telefono || "—"}</p>
          <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {c.email || "—"}</p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {c.indirizzo || "—"}</p>
          {c.codice_fiscale && <p className="font-mono text-xs text-slate-500">CF/P.IVA: {c.codice_fiscale}</p>}
          {c.note && <p className="rounded-md bg-slate-50 p-3 text-slate-600">{c.note}</p>}
        </div>
        <PrivacyCard key={`${c.privacy_firmata}`} c={c} onChange={load} />
        </div>
        <div className="space-y-6">
          <ArchivioDocumenti c={c} negozio={negozio} refreshKey={`${c.privacy_firmata}-${c.privacy_data}`} />
          <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="cliente-bici-list">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h2 className="font-heading text-base font-bold">Bici ({bici.length})</h2>
              <Button size="sm" variant="outline" onClick={() => setBiciOpen(true)} data-testid="cliente-add-bici-button"><Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi bici</Button>
            </div>
            <ul className="divide-y divide-slate-100">
              {bici.map((b) => (
                <li key={b.id} className="px-5 py-3 text-sm">
                  <p className="font-semibold text-slate-900">{b.marca} {b.modello} <span className="font-normal text-slate-500">· {b.tipo || "—"} · {b.colore || "—"}</span></p>
                  <p className="font-mono text-xs text-slate-500">Telaio: {b.telaio || "—"}</p>
                </li>
              ))}
              {bici.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-500">Nessuna bici registrata.</li>}
            </ul>
          </section>
          <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="cliente-schede-list">
            <div className="border-b border-slate-100 px-5 py-3"><h2 className="font-heading text-base font-bold">Storico schede di lavoro ({schede.length})</h2></div>
            <ul className="divide-y divide-slate-100">
              {schede.map((s) => (
                <li key={s.id} className="flex cursor-pointer items-center justify-between px-5 py-3 text-sm hover:bg-slate-50" onClick={() => navigate(`/schede/${s.id}`)} data-testid={`cliente-scheda-${s.id}`}>
                  <div>
                    <span className="font-mono text-xs font-bold text-orange-600">#{s.numero}</span>
                    <p className="text-slate-900">{s.bici_desc} · {dataIt(s.data_ingresso)}</p>
                  </div>
                  <div className="flex items-center gap-3"><span className="font-mono font-bold">{euro(s.totale)}</span><StatusBadge stato={s.stato} /></div>
                </li>
              ))}
              {schede.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-500">Nessuna scheda di lavoro.</li>}
            </ul>
          </section>
        </div>
      </div>
      <EntityFormDialog open={editOpen} onOpenChange={setEditOpen} endpoint="clienti" fields={CLIENTE_FIELDS} item={c} title="Modifica cliente" prefix="cliente-edit-form" onSaved={load} />
      <EntityFormDialog open={biciOpen} onOpenChange={setBiciOpen} endpoint="bici" fields={biciFields([c])} item={{ cliente_id: id }} title="Nuova bici" prefix="cliente-bici-form" onSaved={load} />
    </div>
  );
}
